import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

/**
 * One per-cert-type entry returned by the TikTok Certifications API. The
 * `applicable_categories` array lists the TikTok category ids that drive the
 * requirement; an empty array means the row is an "orphan" registration the
 * sub no longer maps a category to but which still has channel_certifications
 * history we want the operator to see.
 */
export interface CertTypeRequirement {
  cert_type_id: string;
  cert_type_name: string;
  expiration_required: boolean;
  sample_image_url: string | null;
  applicable_categories: string[];
  registrations: CertRegistration[];
}

export interface CertRegistration {
  id: number;
  astur_asset_id: number;
  status: 'pending' | 'registered' | 'failed' | 'expired';
  upload_response_id: string | null;
  expires_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CategoryCertRequirementsData {
  subscription_id: number;
  channel_name: string;
  category_ids: string[];
  required_cert_types: CertTypeRequirement[];
}

/**
 * GET /api/integrations/tiktok/{subscription_id}/certifications
 *
 * Returns the cert types the subscription's catalog categories require,
 * joined to existing channel_certifications registrations for status display.
 */
export type TikTokCertificationRequestOptions = ReadRequestOptions;

export async function listCategoryCertRequirements(
  scope: RequestScope,
  args: { subscriptionId: number },
  options: TikTokCertificationRequestOptions
): Promise<ChannelApiResponse<CategoryCertRequirementsData>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }

    const res = await scopedChannelApi(scope).get<CategoryCertRequirementsData>(`integrations/tiktok/${args.subscriptionId}/certifications`, options);

    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
